import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { money, statusLabel, statusTone } from "@/lib/format";
import { FilePlus2, Search } from "lucide-react";

export default function Transactions() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/transactions");
        setItems(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = items.filter((t) =>
    !q ||
    t.name?.toLowerCase().includes(q.toLowerCase()) ||
    t.property_data?.address?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="transactions-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-2">Ledger</div>
          <h1 className="text-3xl sm:text-4xl font-display">Transactions</h1>
        </div>
        <Link to="/transactions/new" className="btn-brass self-start sm:self-auto" data-testid="new-transaction-cta">
          <FilePlus2 size={16} /> New Offer
        </Link>
      </div>

      <div className="grid-panel">
        <div className="p-4 border-b border-border flex items-center gap-3">
          <Search size={16} className="text-slate-400" />
          <input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by client or address"
            data-testid="txn-search"
            className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-slate-400"
          />
        </div>

        {loading ? (
          <div className="p-10 text-sm text-slate-500 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-sm text-slate-500 text-center">
            {items.length === 0 ? "No transactions yet." : "No results."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-slate-50 border-b border-border">
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Offer</th>
                  <th className="px-4 py-3 text-right">Readiness</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((t) => (
                  <tr key={t.id} className="hover:bg-slate-50 transition-colors" data-testid={`txn-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{t.property_data?.address}</div>
                    </td>
                    <td className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500">
                      {t.transaction_type?.replace(/_/g, " ")}
                    </td>
                    <td className="px-4 py-3 mono-num">{money(t.offer_terms?.offer_price)}</td>
                    <td className="px-4 py-3 mono-num text-brass-700">
                      {t.readiness_score?.overall ? `${t.readiness_score.overall}%` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`badge-status ${statusTone[t.status]}`}>{statusLabel[t.status]}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/transactions/${t.id}`} className="link-brass text-xs" data-testid={`open-txn-${t.id}`}>Open →</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
